import Actor from './actor'
import $ from 'jquery'
import formHelper from '../../components/form-helper'
import DataType from 'scoreboard/datasets/model/data-type'
const { DATE, TIME, DATE_TIME } = DataType
import WidgetState from '../model/widget-state'
const { TEXT_ONLY, DISABLED, VISIBLE } = WidgetState
const { DASHBOARD } = FormWidgetType
import { to24hrTime } from '../../components/date-time-util'
import _ from 'lodash'
import { isEditableRecord } from '../model/form'
import FormWidgetType from '../model/form-widget-type'

/**
 * Changes the state of a widget on the page
 */
class ChangeWidgetStateActor extends Actor {
	act() {
		const action = this.options.action
		const widgetId = action.changeWidgetId
		const state = action.changeWidgetState
		const widgetsView = this.options.formDirector?.widgetsView

		if (!widgetsView) return
		const widget = this.model.widgetsById[widgetId]

		const fieldId = widget?.fieldId
		const fieldDataType =
			this.model?.dataset?.getUnderlyingField(fieldId)?.dataType
		const record = this.options?.formDirector?.record
		const isRecordOrFieldEditable =
			isEditableRecord(fieldId, this.model.dataset, record)

		const fromDom = this.readFromDom(
			widgetsView?.el, isRecordOrFieldEditable
		)

		// maintain changes to this widget before the rerender
		this.maintainFieldValueFromWidget(widget, widgetsView, fieldDataType)
		this.maintainTimeFieldValueFromWidget(
			widget, fromDom, state,
			fieldId, fieldDataType, isRecordOrFieldEditable
		)

		widget.state = state
		// #60292
		// Set a special property for dashboard widgets to determine if they
		// should be call their render function
		if (widget.type === DASHBOARD) {
			widget.renderWidgetImmediately = state === VISIBLE
		}
		widgetsView.updateWidgetView(widget)
	}

	/**
	 * #55952
	 * Formats the value depending on which state the widget is transitioning
	 * from and to. This is necessary because certain widget states parse time
	 * values differently
	 * @param widget - the widget whose state is being changed
	 * @param fromDom - the values read from the widget in the DOM
	 * @param newState - the state the widget is transitioning to
	 * @param fieldId - the field ID of the widget's field
	 * @param fieldDataType - the field's data type
	 * @param isRecordOrFieldEditable - whether the record or field is editable
	 */
	maintainTimeFieldValueFromWidget(
		widget, fromDom, newState,
		fieldId, fieldDataType, isRecordOrFieldEditable
	) {
		const prevState = widget.state // State before the change

		// TEXT_ONLY and DISABLED states are, currently, the only ones that
		// need to be given special consideration
		if ([TEXT_ONLY, DISABLED].includes(prevState) &&
			[DATE, TIME, DATE_TIME].includes(fieldDataType)
		) {
			let value = fromDom?.[fieldId]
			value = this.formatWidgetValue(
				fieldDataType, prevState, newState,
				value, isRecordOrFieldEditable
			)

			this.options.formDirector.setFieldValue(fieldId, value)
		}
	}

	/**
	 * Formats the widget's value based on its various widget and field states
	 * @param fieldDataType - the field's data type
	 * @param prevState - the state of the widget before the change
	 * @param newState - the state of the widget after the change
	 * @param value - the current value of the widget
	 * @param isRecordOrFieldEditable - whether the record or field is editable
	 * @returns {string} - the formatted value
	 */
	formatWidgetValue(
		fieldDataType, prevState, newState,
		value, isRecordOrFieldEditable
	) {
		const isDateField = [DATE, DATE_TIME].includes(fieldDataType)
		const isTextOnlyToTextOnlyTransition =
			prevState === TEXT_ONLY && newState === TEXT_ONLY

		// #56384
		// Return early with an empty string if the value isn't valid
		if (!this.isValidTimeString(value)) return ''

		// #56384
		// Widgets with DATE/DATE_TIME fields require ISO formatting when ALL
		// the following conditions are met:
		// 1. The widget's field is editable
		// 2. The widget was not previously in the DISABLED state
		// 3. The widget is not transitioning from TEXT_ONLY to TEXT_ONLY
		if (isDateField &&
			isRecordOrFieldEditable &&
			prevState != DISABLED &&
			!isTextOnlyToTextOnlyTransition
		) {
			value = new Date(value)?.toISOString()
		} else if (fieldDataType === TIME) {
			value = to24hrTime(value)
		}
		return value
	}

	/**
	 * An override of the default formHelper.readFromDom() that
	 * also reads the value of text-only and disabled widgets
	 * @param el - the widget's DOM element
	 * @param isRecordOrFieldEditable - whether the record or field is editable
	 * @returns fromDom - the values read from the widgets in the DOM
	 */
	readFromDom(el, isRecordOrFieldEditable) {
		let fromDom = formHelper.readFromDom(el)

		// #56384
		// Additional read of text-only and disabled widgets to ensure
		// the DOM is read correctly
		$(el).find('.is-text-only:visible, .is-disabled:visible')
			.each((index, el) => {
				// Disabled, editable widgets have their value read correctly
				// in readFromDom(), so we do not want to override the value
				if ($(el).hasClass('is-disabled') &&
					isRecordOrFieldEditable
				) return

				let control = $(el).find('.field-value-control')
				let name = control.data('id')
				fromDom[name] = $(el).find('.widget-value').text()
			})

		return fromDom
	}

	/**
	 * Determines whether a string is null, undefined, or empty and does not
	 * equal the string 'Blank'; otherwise, we can assume the string is valid
	 * if it's in a DATE/TIME/DATE_TIME field
	 * Primarily used to ensure we do not pass in values that will be
	 * considered invalid by DATE/TIME/DATE_TIME fields
	 * @param str - the time string to check
	 * @returns {boolean} - whether the string is NOT null, undefined, or empty
	 * and does NOT equal the string 'Blank'
	 */
	isValidTimeString(str) {
		return !_.isNil(str) && !_.isEmpty(_.trim(str)) && str !== 'Blank'
	}
}

export default ChangeWidgetStateActor
ChangeWidgetStateActor.__filename__ = 'forms/actions/change-widget-state-actor.js'
